'use client';

import { memo, useCallback, useState } from 'react';

import Image from 'next/image';
import { useTranslations } from 'next-intl';

import CheckIcon from '@/assets/icons/check.svg';
import EyeIcon from '@/assets/icons/eye.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { I_Avatar } from '@/data/editProfileMock';
import { cn } from '@/lib/utils/cn';

// Types
type T_AvatarGridProps = {
  avatars: I_Avatar[];
  selectedAvatar: I_Avatar | null;
  onAvatarSelect: (avatar: I_Avatar) => void;
  onPreview?: (avatar: I_Avatar) => void;
  isLoading?: boolean;
};

type T_AvatarItemProps = {
  avatar: I_Avatar;
  selectedAvatar: I_Avatar | null;
  onAvatarSelect: (avatar: I_Avatar) => void;
  onPreview: (avatar: I_Avatar) => void;
};

// Individual Avatar Item Component with loading state
const AvatarItem = memo(function AvatarItem({
  avatar,
  selectedAvatar,
  onAvatarSelect,
  onPreview
}: T_AvatarItemProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleAvatarSelect = useCallback(() => {
    onAvatarSelect(avatar);
  }, [avatar, onAvatarSelect]);

  const handlePreview = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onPreview(avatar);
    },
    [avatar, onPreview]
  );

  return (
    <div
      className={cn(
        'relative w-17 h-17 aspect-square rounded-lg overflow-hidden transition-all'
      )}
    >
      {/* Loading skeleton */}
      {isLoading && <Skeleton className="w-17 h-17 rounded-lg" />}

      {/* Error state */}
      {hasError && (
        <div className="w-17 h-17 rounded-lg bg-muted flex items-center justify-center">
          <P className="text-xs text-muted-foreground">Error</P>
        </div>
      )}

      {/* Image */}
      {!hasError && (
        <Image
          src={avatar.url}
          alt={`Avatar ${avatar.uuid}`}
          className={cn(
            'w-17 h-17 object-cover transition-opacity duration-200',
            isLoading ? 'opacity-0' : 'opacity-100'
          )}
          width={68}
          height={68}
          priority
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}

      {/* Selected overlay */}
      {!isLoading && !hasError && selectedAvatar?.uuid === avatar.uuid && (
        <div className="absolute inset-0 rounded-lg bg-primary/70 border-2 border-primary-border transition duration-200 flex items-center justify-center z-10">
          <Button
            variant="link"
            size="icon"
            className="focus:ring-0 w-10 h-10 transition-all duration-200"
            onClick={handlePreview}
            icon={<CheckIcon className="w-10 h-10 text-foreground" />}
          />
        </div>
      )}

      {/* Hover overlay */}
      {!isLoading && !hasError && (
        <div className="absolute inset-0 rounded-lg opacity-0 hover:opacity-100 hover:bg-primary/70 hover:border-2 hover:border-primary-border group-hover:opacity-100 transition duration-200 flex items-center justify-center z-10">
          <Button
            variant="link"
            size="icon"
            className="focus:ring-0 w-10 h-10 hover:opacity-100 transition-all duration-200"
            onClick={handlePreview}
            icon={<EyeIcon className="text-foreground" />}
          />
        </div>
      )}

      {/* Click to select */}
      <div className="absolute inset-0" onClick={handleAvatarSelect} />
    </div>
  );
});

export const AvatarGrid = memo(function AvatarGrid({
  avatars,
  selectedAvatar,
  onAvatarSelect,
  onPreview,
  isLoading = false
}: T_AvatarGridProps) {
  const t = useTranslations('editProfile.avatar');

  const handleAvatarSelect = useCallback(
    (avatar: I_Avatar) => {
      onAvatarSelect(avatar);
    },
    [onAvatarSelect]
  );

  const handlePreview = useCallback(
    (avatar: I_Avatar) => {
      onPreview?.(avatar);
    },
    [onPreview]
  );

  if (isLoading) {
    return (
      <div className="min-h-17 grid grid-cols-4 md:grid-cols-8 gap-4 w-full">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} className="w-17 h-17 rounded-lg" />
        ))}
      </div>
    );
  }

  if (avatars.length === 0) {
    return (
      <div className="min-h-17 flex items-center justify-center">
        <P className="text-muted-foreground">{t('noAvatars')}</P>
      </div>
    );
  }

  return (
    <div className="min-h-17 grid grid-cols-4 md:grid-cols-8 gap-4 w-full">
      {avatars.map(avatar => (
        <AvatarItem
          key={avatar.uuid}
          avatar={avatar}
          selectedAvatar={selectedAvatar}
          onAvatarSelect={handleAvatarSelect}
          onPreview={handlePreview}
        />
      ))}
    </div>
  );
});
